import { GeoDiscount } from 'shared/types/cart/Discount';
import { AlertCode } from 'shared/types/checkout';

const GEO_ELIGIBILITY_ERROR_CODES = new Set<string>([
  AlertCode.COUPON_NOT_ELIGIBLE_FOR_CART,
  AlertCode.COUPON_NOT_ELIGIBLE_FOR_BUNDLED_PURCHASE,
  AlertCode.COUPON_NOT_VALID_FOR_CART,
]);

/**
 * Maps the reason a geo discount resolved to the banner shown when it cannot be
 * applied. Partner and product exclusions both mean the cart's offering does not
 * qualify, so they share the course/program copy; `fallbackCode` covers callers
 * that can tell location is the reason. When a redemption was attempted, that
 * copy is only used for an eligibility failure; unrelated failures use the
 * general message.
 */
export const getGeoDiscountFailureAlert = (
  discount: GeoDiscount | null | undefined,
  fallbackCode: AlertCode = AlertCode.GEO_DISCOUNT_APPLY_FAILED,
  redemptionErrorCode?: string | null,
): { code: AlertCode } => {
  const isRedemptionFailure = redemptionErrorCode !== undefined;
  const isEligibilityFailure =
    typeof redemptionErrorCode === 'string' &&
    GEO_ELIGIBILITY_ERROR_CODES.has(redemptionErrorCode);

  if (isRedemptionFailure && !isEligibilityFailure) {
    return { code: AlertCode.GEO_DISCOUNT_APPLY_FAILED };
  }

  if (discount?.source === 'product' || discount?.source === 'partner') {
    return {
      code:
        discount.productKind === 'program'
          ? AlertCode.GEO_PROGRAM_INELIGIBLE
          : AlertCode.GEO_COURSE_INELIGIBLE,
    };
  }

  return { code: fallbackCode };
};
